/*
 * Client for the backend's multiplexed SSE endpoint (`GET /api/events`).
 *
 * `EventSource` cannot send an Authorization header, so this reads the stream
 * with `fetch` and parses SSE frames by hand. One connection per tab: the
 * scopes (which case / client / practice threads to follow, keyed by the owner
 * that claimed them, so the Case IDE pane and the agent panel can both be
 * live) become repeated `scope=` query params, and changing them reconnects.
 * Notifications for the signed-in user are always included.
 *
 * Every frame carries `id: <cursor>`; on reconnect it is sent back as
 * `Last-Event-ID` so the server replays messages missed in the gap. The server
 * ends each stream after ~55s with a `reconnect` event (we reconnect at once);
 * a failed connection backs off 1s → 30s. A hidden tab pauses the stream and
 * resumes (with its cursor) when shown again. No React here so it can be
 * tested with a fake `fetch`.
 */

export type RealtimeEvent = {
  type: string;
  data: unknown;
  /** Server clock at emit time (seconds). */
  ts?: number;
  /** Frame id / resume cursor. */
  id?: string | null;
};

export type ScopeKind = "case" | "client" | "practice";

export type StreamScope = {
  /** Defaults to `"case"`. */
  kind?: ScopeKind;
  /** The case, client or practice id. */
  scopeId?: string | null;
  /** Legacy alias for `{ kind: "case", scopeId }`. */
  individualId?: string | null;
  threadId?: string | null;
  /** Replay messages newer than this on (re)connect. */
  afterNs?: number | null;
};

/** `scope=<kind>:<id>[:<thread>]` for one scope, or `null` when it names nothing. */
export const scopeParam = (scope: StreamScope): string | null => {
  const kind = scope.kind ?? "case";
  const id = scope.scopeId ?? (kind === "case" ? scope.individualId : null);
  if (!id) return null;
  return scope.threadId ? `${kind}:${id}:${scope.threadId}` : `${kind}:${id}`;
};

export type StreamStatus = "idle" | "connecting" | "open" | "closed" | "paused";

type Listener = (event: RealtimeEvent) => void;
type StatusListener = (status: StreamStatus) => void;

export type EventStreamOptions = {
  /** Absolute URL of the events endpoint. */
  url: string;
  getToken: () => Promise<string | null>;
  fetchImpl?: typeof fetch;
  /** Reconnect backoff bounds (ms). */
  minBackoffMs?: number;
  maxBackoffMs?: number;
};

/** Parse one `\n\n`-delimited SSE block. Comments (`: ...`) return null. */
export const parseSseBlock = (block: string): RealtimeEvent | null => {
  let event = "message";
  let id: string | null = null;
  const dataLines: string[] = [];
  for (const raw of block.split("\n")) {
    const line = raw.replace(/\r$/, "");
    if (line && !line.startsWith(":")) {
      const colon = line.indexOf(":");
      const field = colon < 0 ? line : line.slice(0, colon);
      let value = colon < 0 ? "" : line.slice(colon + 1);
      if (value.startsWith(" ")) value = value.slice(1);
      if (field === "event") event = value;
      else if (field === "data") dataLines.push(value);
      else if (field === "id") id = value;
    }
  }
  if (!dataLines.length) return null;
  const rawData = dataLines.join("\n");
  let payload: unknown;
  try {
    payload = JSON.parse(rawData);
  } catch {
    payload = rawData;
  }
  const typed = payload as { type?: string; data?: unknown; ts?: number };
  const isEnvelope =
    !!typed && typeof typed === "object" && "type" in typed && "data" in typed;
  return {
    type: isEnvelope && typed.type ? typed.type : event,
    data: isEnvelope ? typed.data : payload,
    ts: isEnvelope ? typed.ts : undefined,
    id
  };
};

/**
 * Query for a set of scopes. `after_ns` is sent only when exactly one scope
 * follows a thread (the server replays a single thread's gap; with several the
 * clients reload).
 */
export const scopeQuery = (scopes: StreamScope | StreamScope[]): string => {
  const list = Array.isArray(scopes) ? scopes : [scopes];
  const q = new URLSearchParams();
  const seen = new Set<string>();
  const withThread: StreamScope[] = [];
  for (const scope of list) {
    const param = scopeParam(scope);
    if (param && !seen.has(param)) {
      seen.add(param);
      q.append("scope", param);
      if (scope.threadId) withThread.push(scope);
    }
  }
  if (withThread.length === 1) {
    const { afterNs } = withThread[0];
    if (afterNs != null && Number.isFinite(afterNs))
      q.set("after_ns", String(afterNs));
  }
  const s = q.toString();
  return s ? `?${s}` : "";
};

/** The connection identity: which scopes (and threads) are followed. */
const scopesKey = (scopes: Iterable<StreamScope>): string =>
  Array.from(scopes, scopeParam)
    .filter((p): p is string => !!p)
    .sort()
    .join("|");

export class EventStreamClient {
  /** Scopes by the owner that claimed them (`"case-ide"`, `"agent-panel"`, ...). */
  private scopes = new Map<string, StreamScope>();

  private listeners = new Set<Listener>();

  private statusListeners = new Set<StatusListener>();

  private controller: AbortController | null = null;

  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;

  private backoff: number;

  private running = false;

  private paused = false;

  private generation = 0;

  /** Last frame id seen; replayed as `Last-Event-ID` on reconnect. */
  lastEventId: string | null = null;

  status: StreamStatus = "idle";

  constructor(private readonly opts: EventStreamOptions) {
    this.backoff = opts.minBackoffMs ?? 1000;
  }

  subscribe(listener: Listener): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  onStatus(listener: StatusListener): () => void {
    this.statusListeners.add(listener);
    listener(this.status);
    return () => this.statusListeners.delete(listener);
  }

  /**
   * Follow a scope on behalf of `owner` (`null` releases it). A change in the
   * set of followed scopes/threads reconnects with a fresh cursor; a changed
   * `afterNs` alone does not.
   */
  setScope(owner: string, next: StreamScope | null): void {
    const before = scopesKey(this.scopes.values());
    if (next && scopeParam(next)) this.scopes.set(owner, { ...next });
    else this.scopes.delete(owner);
    const changed = before !== scopesKey(this.scopes.values());
    if (changed) {
      // The cursor belongs to the old thread; the new scope's `afterNs` (if
      // any) is the resume point.
      this.lastEventId = null;
      if (this.running && !this.paused) this.restart();
    }
  }

  /** The scopes currently followed (test seam). */
  currentScopes(): StreamScope[] {
    return Array.from(this.scopes.values());
  }

  start(): void {
    if (this.running) return;
    this.running = true;
    if (!this.paused) void this.connect();
  }

  stop(): void {
    this.running = false;
    this.abort();
    this.setStatus("idle");
  }

  /** Hidden tab: drop the connection but keep the cursor for the resume. */
  pause(): void {
    if (this.paused) return;
    this.paused = true;
    if (!this.running) return;
    this.abort();
    this.setStatus("paused");
  }

  resume(): void {
    if (!this.paused) return;
    this.paused = false;
    if (!this.running) return;
    this.backoff = this.opts.minBackoffMs ?? 1000;
    void this.connect();
  }

  private abort(): void {
    this.clearTimer();
    this.generation += 1;
    this.controller?.abort();
    this.controller = null;
  }

  private restart(): void {
    this.abort();
    this.backoff = this.opts.minBackoffMs ?? 1000;
    void this.connect();
  }

  private clearTimer(): void {
    if (this.reconnectTimer) clearTimeout(this.reconnectTimer);
    this.reconnectTimer = null;
  }

  private setStatus(status: StreamStatus): void {
    if (this.status === status) return;
    this.status = status;
    this.statusListeners.forEach((l) => l(status));
  }

  private emit(event: RealtimeEvent): void {
    this.listeners.forEach((l) => {
      try {
        l(event);
      } catch {
        // A bad listener must not take the stream down.
      }
    });
  }

  private scheduleReconnect(immediate: boolean): void {
    if (!this.running || this.paused) return;
    this.clearTimer();
    const delay = immediate ? 0 : this.backoff;
    if (!immediate) {
      this.backoff = Math.min(
        this.backoff * 2,
        this.opts.maxBackoffMs ?? 30_000
      );
    }
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      void this.connect();
    }, delay);
  }

  private async connect(): Promise<void> {
    if (!this.running || this.paused) return;
    this.generation += 1;
    const gen = this.generation;
    const controller = new AbortController();
    this.controller = controller;
    this.setStatus("connecting");
    const fetchImpl = this.opts.fetchImpl ?? fetch;
    let cleanClose = false;
    try {
      const token = await this.opts.getToken();
      if (gen !== this.generation) return;
      const headers: Record<string, string> = {
        Accept: "text/event-stream"
      };
      if (token) headers.Authorization = `Bearer ${token}`;
      if (this.lastEventId) headers["Last-Event-ID"] = this.lastEventId;
      // Default cache mode on purpose: with `no-store` Chromium also skips the
      // CORS preflight cache, so each reconnect would pay an OPTIONS. The
      // server's `Cache-Control: no-cache` keeps the stream itself uncached.
      const resp = await fetchImpl(
        `${this.opts.url}${scopeQuery(this.currentScopes())}`,
        { headers, signal: controller.signal }
      );
      if (gen !== this.generation) return;
      if (!resp.ok || !resp.body) {
        // 4xx (a deleted thread, lost access): do not hammer the server.
        if (resp.status >= 400 && resp.status < 500 && resp.status !== 401) {
          this.setStatus("closed");
          this.emit({ type: "$error", data: { status: resp.status } });
          return;
        }
        throw new Error(`events stream ${resp.status}`);
      }
      this.setStatus("open");
      this.backoff = this.opts.minBackoffMs ?? 1000;
      const reader = resp.body.getReader();
      const decoder = new TextDecoder("utf-8");
      let buffer = "";
      // eslint-disable-next-line no-constant-condition
      while (true) {
        // eslint-disable-next-line no-await-in-loop
        const { value, done } = await reader.read();
        if (gen !== this.generation) return;
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const blocks = buffer.split(/\r?\n\r?\n/);
        buffer = blocks.pop() ?? "";
        for (const block of blocks) {
          const ev = parseSseBlock(block);
          if (ev) {
            if (ev.id) this.lastEventId = ev.id;
            if (ev.type === "reconnect") cleanClose = true;
            else if (ev.type !== "heartbeat") this.emit(ev);
          }
        }
      }
      cleanClose = true;
    } catch (e) {
      if (gen !== this.generation) return;
      if ((e as Error)?.name === "AbortError") return;
    } finally {
      if (gen === this.generation && this.controller === controller) {
        this.controller = null;
      }
    }
    if (gen !== this.generation || !this.running) return;
    this.setStatus("closed");
    this.scheduleReconnect(cleanClose);
  }
}
